/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    ItemFragment,
    PickLocationFragment,
    ProjectLineItemFragment,
    ProjectsService,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { ProjectStore } from './project.store';
import {
    ProjectCreate,
    ProjectRead,
    ProjectSearchParams,
} from '../../entities/project.dto';
import { Operation } from 'fast-json-patch';
import { asArraySafe } from '@pf/shared-utility';
import { ProjectLineItemStore } from '../project-line-items/project-line-item.store';
import { PickStore } from '../picks/pick.store';
import { InventoryBundleStore } from '../inventory-bundles/inventory-bundle.store';
import { ContainerStore } from '../containers/container.store';
import { SlotStore } from '../slots/slot.store';
import { ZoneStore } from '../zones/zone.store';
import { ProjectLineItemRead } from '../../entities/project-line-item.dto';
import { PickRead } from '../../entities/pick.dto';
import { InventoryBundleRead } from '../../entities/inventory-bundle.dto';
import { ContainerRead } from '../../entities/container.dto';
import { SlotRead } from '../../entities/slot.dto';
import { ZoneRead } from '../../entities/zone.dto';
import { ItemRead, ItemStore } from '@pf/platform-item-catalog/domain';

const projectIncludes = 'withPicks,withPackingLists';

@Injectable()
export class ProjectDataService extends AbstractDataService<
    ProjectRead,
    ProjectCreate
> {
    private readonly projectLineItemStore = inject(ProjectLineItemStore);
    private readonly pickStore = inject(PickStore);
    private readonly inventoryBundleStore = inject(InventoryBundleStore);
    private readonly containerStore = inject(ContainerStore);
    private readonly slotStore = inject(SlotStore);
    private readonly zoneStore = inject(ZoneStore);
    private readonly itemStore = inject(ItemStore);

    protected EntityName = PFWmsEntities.Project;
    constructor(
        private service: ProjectsService,
        store: ProjectStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(body: ProjectCreate): Observable<ProjectRead> {
        return this.service.projectCreate({
            projectViewModelCreateBody: body,
        }) as Observable<ProjectRead>;
    }
    protected deleteObs$(id: string): Observable<ProjectRead> {
        return this.service.projectDelete({
            id,
        }) as Observable<ProjectRead>;
    }
    protected undoDeleteObs$(id: string): Observable<ProjectRead> {
        return this.service.projectUndoDelete({
            id,
        }) as Observable<ProjectRead>;
    }
    protected getObs$(id: string): Observable<ProjectRead> {
        return this.service
            .projectReadById({
                id,
                include: projectIncludes,
            })
            .pipe(
                map(project => project as ProjectRead),
                tap(project => this.addProjectLineItemsToStore(project))
            ) as Observable<ProjectRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<ProjectRead> {
        return this.service.projectUpdatePatch({
            id,
            operation: operations,
        }) as Observable<ProjectRead>;
    }
    protected searchObs$(
        searchParams: ProjectSearchParams
    ): Observable<IPagedResult<ProjectRead>> {
        return this.service
            .projectSearch({
                ...searchParams,
                include: projectIncludes,
            })
            .pipe(
                map(result => result as IPagedResult<ProjectRead>),
                tap(result =>
                    asArraySafe(result.data).forEach(project =>
                        this.addProjectLineItemsToStore(project)
                    )
                )
            ) as Observable<IPagedResult<ProjectRead>>;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<ProjectRead>>> {
        return this.service.projectGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<ProjectRead>>
        >;
    }

    private addProjectLineItemsToStore(project: ProjectRead) {
        try {
            asArraySafe(project.lineItems).forEach(lineItem => {
                this.projectLineItemStore.add(
                    lineItem as ProjectLineItemRead,
                    false
                );
                this.addPickToStore(lineItem);
            });
        } catch (error) {
            console.error(error);
        }
    }

    private addPickToStore(lineItem: ProjectLineItemFragment) {
        if (lineItem.pick) {
            this.pickStore.add(lineItem.pick as PickRead, false);

            asArraySafe(lineItem.pick.pickLocations).forEach(location => {
                if (!this.itemStore.get(lineItem.itemId as string)) {
                    this.itemStore.add(lineItem.item as ItemRead, false);
                }
                this.addPickLocationEntitiesToStore(
                    location,
                    lineItem.item as ItemFragment
                );
            });
        }
    }

    private addPickLocationEntitiesToStore(
        location: PickLocationFragment,
        item: ItemFragment
    ) {
        if (!location.inventoryBundle) {
            return;
        }

        location.inventoryBundle.item = item;
        this.inventoryBundleStore.add(
            location.inventoryBundle as InventoryBundleRead,
            false
        );

        if (!location.inventoryBundle.container) {
            return;
        }

        this.containerStore.add(
            location.inventoryBundle.container as ContainerRead,
            false
        );

        if (!location.inventoryBundle.container.slot) {
            return;
        }

        this.slotStore.add(
            location.inventoryBundle.container.slot as SlotRead,
            false
        );

        if (!location.inventoryBundle.container.slot.zone) {
            return;
        }

        this.zoneStore.add(
            location.inventoryBundle.container.slot.zone as ZoneRead,
            false
        );
    }
}
